// Curated Lucide icon catalog for placemarks. Each icon is statically
// imported so webpack tree-shakes the rest of Lucide's ~1700-icon set.
// Lucide ships each icon as `[tagName, attrs][]` (see
// node_modules/lucide/dist/esm/icons/<name>.mjs); we synthesise an SVG
// envelope around that array on demand and feed it through the same
// DOMParser → Path2D pipeline the renderer uses for every other glyph.
//
// To add an icon: pick a Lucide name from https://lucide.dev, import it
// here (the file path is kebab-case; the binding is PascalCase), add it
// to ICONS, and slot it into a category in iconCategories.ts.
import Anchor from 'lucide/dist/esm/icons/anchor';
import AlertTriangle from 'lucide/dist/esm/icons/triangle-alert';
import Axe from 'lucide/dist/esm/icons/axe';
import Bike from 'lucide/dist/esm/icons/bike';
import Binoculars from 'lucide/dist/esm/icons/binoculars';
import Bird from 'lucide/dist/esm/icons/bird';
import Birdhouse from 'lucide/dist/esm/icons/birdhouse';
import Bookmark from 'lucide/dist/esm/icons/bookmark';
import Bug from 'lucide/dist/esm/icons/bug';
import Building from 'lucide/dist/esm/icons/building';
import Building2 from 'lucide/dist/esm/icons/building-2';
import Bus from 'lucide/dist/esm/icons/bus';
import Car from 'lucide/dist/esm/icons/car';
import CarFront from 'lucide/dist/esm/icons/car-front';
import Caravan from 'lucide/dist/esm/icons/caravan';
import Castle from 'lucide/dist/esm/icons/castle';
import Cat from 'lucide/dist/esm/icons/cat';
import Cherry from 'lucide/dist/esm/icons/cherry';
import Church from 'lucide/dist/esm/icons/church';
import Circle from 'lucide/dist/esm/icons/circle';
import CircleParking from 'lucide/dist/esm/icons/circle-parking';
import Coffee from 'lucide/dist/esm/icons/coffee';
import Compass from 'lucide/dist/esm/icons/compass';
import Construction from 'lucide/dist/esm/icons/construction';
import Crosshair from 'lucide/dist/esm/icons/crosshair';
import Diamond from 'lucide/dist/esm/icons/diamond';
import Dog from 'lucide/dist/esm/icons/dog';
import DoorClosed from 'lucide/dist/esm/icons/door-closed';
import DoorOpen from 'lucide/dist/esm/icons/door-open';
import Droplet from 'lucide/dist/esm/icons/droplet';
import Droplets from 'lucide/dist/esm/icons/droplets';
import Factory from 'lucide/dist/esm/icons/factory';
import Fish from 'lucide/dist/esm/icons/fish';
import FishSymbol from 'lucide/dist/esm/icons/fish-symbol';
import FishingHook from 'lucide/dist/esm/icons/fishing-hook';
import FishingRod from 'lucide/dist/esm/icons/fishing-rod';
import Flag from 'lucide/dist/esm/icons/flag';
import Flame from 'lucide/dist/esm/icons/flame';
import Flower from 'lucide/dist/esm/icons/flower';
import Flower2 from 'lucide/dist/esm/icons/flower-2';
import Footprints from 'lucide/dist/esm/icons/footprints';
import Fuel from 'lucide/dist/esm/icons/fuel';
import Grape from 'lucide/dist/esm/icons/grape';
import Hammer from 'lucide/dist/esm/icons/hammer';
import Heart from 'lucide/dist/esm/icons/heart';
import Hospital from 'lucide/dist/esm/icons/hospital';
import Hotel from 'lucide/dist/esm/icons/hotel';
import House from 'lucide/dist/esm/icons/house';
import Landmark from 'lucide/dist/esm/icons/landmark';
import Leaf from 'lucide/dist/esm/icons/leaf';
import LeafyGreen from 'lucide/dist/esm/icons/leafy-green';
import Library from 'lucide/dist/esm/icons/library';
import LifeBuoy from 'lucide/dist/esm/icons/life-buoy';
import Map from 'lucide/dist/esm/icons/map';
import MapPin from 'lucide/dist/esm/icons/map-pin';
import MapPinHouse from 'lucide/dist/esm/icons/map-pin-house';
import MapPinned from 'lucide/dist/esm/icons/map-pinned';
import Mountain from 'lucide/dist/esm/icons/mountain';
import MountainSnow from 'lucide/dist/esm/icons/mountain-snow';
import Navigation from 'lucide/dist/esm/icons/navigation';
import OctagonAlert from 'lucide/dist/esm/icons/octagon-alert';
import ParkingMeter from 'lucide/dist/esm/icons/parking-meter';
import Phone from 'lucide/dist/esm/icons/phone';
import Pickaxe from 'lucide/dist/esm/icons/pickaxe';
import Pin from 'lucide/dist/esm/icons/pin';
import Plane from 'lucide/dist/esm/icons/plane';
import Plus from 'lucide/dist/esm/icons/plus';
import Rabbit from 'lucide/dist/esm/icons/rabbit';
import Sailboat from 'lucide/dist/esm/icons/sailboat';
import School from 'lucide/dist/esm/icons/school';
import Shell from 'lucide/dist/esm/icons/shell';
import ShieldAlert from 'lucide/dist/esm/icons/shield-alert';
import Ship from 'lucide/dist/esm/icons/ship';
import ShoppingBag from 'lucide/dist/esm/icons/shopping-bag';
import ShoppingCart from 'lucide/dist/esm/icons/shopping-cart';
import Shovel from 'lucide/dist/esm/icons/shovel';
import Siren from 'lucide/dist/esm/icons/siren';
import Skull from 'lucide/dist/esm/icons/skull';
import Sprout from 'lucide/dist/esm/icons/sprout';
import SquareParking from 'lucide/dist/esm/icons/square-parking';
import Squirrel from 'lucide/dist/esm/icons/squirrel';
import Star from 'lucide/dist/esm/icons/star';
import Stethoscope from 'lucide/dist/esm/icons/stethoscope';
import Store from 'lucide/dist/esm/icons/store';
import Target from 'lucide/dist/esm/icons/target';
import Tent from 'lucide/dist/esm/icons/tent';
import TentTree from 'lucide/dist/esm/icons/tent-tree';
import Tractor from 'lucide/dist/esm/icons/tractor';
import Train from 'lucide/dist/esm/icons/tram-front';
import TreeDeciduous from 'lucide/dist/esm/icons/tree-deciduous';
import TreePalm from 'lucide/dist/esm/icons/tree-palm';
import TreePine from 'lucide/dist/esm/icons/tree-pine';
import Trees from 'lucide/dist/esm/icons/trees';
import Triangle from 'lucide/dist/esm/icons/triangle';
import Truck from 'lucide/dist/esm/icons/truck';
import Utensils from 'lucide/dist/esm/icons/utensils';
import Warehouse from 'lucide/dist/esm/icons/warehouse';
import Waves from 'lucide/dist/esm/icons/waves-horizontal';
import Wifi from 'lucide/dist/esm/icons/wifi';
import Worm from 'lucide/dist/esm/icons/worm';
import Wrench from 'lucide/dist/esm/icons/wrench';
const ICONS = {
    AlertTriangle,
    Anchor,
    Axe,
    Bike,
    Binoculars,
    Bird,
    Birdhouse,
    Bookmark,
    Bug,
    Building,
    Building2,
    Bus,
    Car,
    CarFront,
    Caravan,
    Castle,
    Cat,
    Cherry,
    Church,
    Circle,
    CircleParking,
    Coffee,
    Compass,
    Construction,
    Crosshair,
    Diamond,
    Dog,
    DoorClosed,
    DoorOpen,
    Droplet,
    Droplets,
    Factory,
    Fish,
    FishSymbol,
    FishingHook,
    FishingRod,
    Flag,
    Flame,
    Flower,
    Flower2,
    Footprints,
    Fuel,
    Grape,
    Hammer,
    Heart,
    Hospital,
    Hotel,
    House,
    Landmark,
    Leaf,
    LeafyGreen,
    Library,
    LifeBuoy,
    Map,
    MapPin,
    MapPinHouse,
    MapPinned,
    Mountain,
    MountainSnow,
    Navigation,
    OctagonAlert,
    ParkingMeter,
    Phone,
    Pickaxe,
    Pin,
    Plane,
    Plus,
    Rabbit,
    Sailboat,
    School,
    Shell,
    ShieldAlert,
    Ship,
    ShoppingBag,
    ShoppingCart,
    Shovel,
    Siren,
    Skull,
    Sprout,
    SquareParking,
    Squirrel,
    Star,
    Stethoscope,
    Store,
    Target,
    Tent,
    TentTree,
    Tractor,
    Train,
    TreeDeciduous,
    TreePalm,
    TreePine,
    Trees,
    Triangle,
    Truck,
    Utensils,
    Warehouse,
    Waves,
    Wifi,
    Worm,
    Wrench,
};
const renderAttrs = (attrs) => Object.entries(attrs)
    .filter(([key]) => key !== 'key')
    .map(([key, value]) => `${key}="${String(value)}"`)
    .join(' ');
// Lucide's canonical SVG envelope: 24×24 viewBox, no fill, stroked paths.
// We inline `fill="none"` and `stroke="currentColor"` on the root so the
// renderer can read fill/stroke from the SVG element when child shapes
// don't specify their own.
export const getLucideSvgText = (iconName) => {
    const node = ICONS[iconName];
    if (!node)
        return undefined;
    const body = node
        .map(([tag, attrs]) => `<${tag} ${renderAttrs(attrs)}/>`)
        .join('');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${body}</svg>`;
};
export const hasLucideIcon = (iconName) => Object.prototype.hasOwnProperty.call(ICONS, iconName);
let cachedNames = null;
export const listLucideIconNames = () => {
    if (!cachedNames)
        cachedNames = Object.keys(ICONS).sort();
    return cachedNames;
};
